import { useMemo, useState } from 'react';
import { api } from '../api';
import { LeadScoreBadge } from '../components/LeadScoreBadge';
import {
  APPOINTMENT_OUTCOME_COLORS,
  APPOINTMENT_OUTCOME_LABELS,
  OPPORTUNITY_EVENT_LABELS,
  OPPORTUNITY_STATUSES,
  OPPORTUNITY_STATUS_COLORS,
  OPPORTUNITY_STATUS_LABELS,
  type LocationSummary,
  type OpportunitiesResponse,
  type OpportunityFilterState,
  type OpportunityStatus,
  type OpportunitySummaryResponse,
} from '../types';
import { formatCompact, formatDateTime, formatNumber, formatPercent } from '../format';
import { StatTile } from '../components/StatTile';
import { StackedColumnChart } from '../components/StackedColumnChart';
import { HorizontalBarChart, type BarDatum } from '../components/HorizontalBarChart';
import { JourneyDrawer } from '../components/JourneyDrawer';
import { ContactJourneyDrawer } from '../components/ContactJourneyDrawer';

const STATUS_SERIES = OPPORTUNITY_STATUSES.map((status) => ({
  key: status,
  label: OPPORTUNITY_STATUS_LABELS[status],
  color: OPPORTUNITY_STATUS_COLORS[status],
}));

function StatusTag({ status }: { status: OpportunityStatus }) {
  return (
    <span className="tag">
      <span className="dot" style={{ background: OPPORTUNITY_STATUS_COLORS[status] }} aria-hidden="true" />
      {OPPORTUNITY_STATUS_LABELS[status]}
    </span>
  );
}

interface Props {
  summary: OpportunitySummaryResponse | null;
  list: OpportunitiesResponse | null;
  loading: boolean;
  locations: LocationSummary[];
  filters: OpportunityFilterState;
  onFiltersChange: (next: OpportunityFilterState) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

function relativeChange(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return (current - previous) / previous;
}

export function Opportunities({
  summary,
  list,
  loading,
  filters,
  onFiltersChange,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: Props) {
  const [journeyId, setJourneyId] = useState<string | null>(null);
  const [journeyContactId, setJourneyContactId] = useState<string | null>(null);

  const stageBars = useMemo<BarDatum[]>(() => {
    if (!summary) return [];
    return summary.byStage.slice(0, 15).map((row) => ({
      key: row.stageIds ?? 'none',
      label: row.stageName ?? 'No stage',
      sublabel: row.pipelineName ?? undefined,
      value: row.total,
      tooltipRows: [
        { label: 'Opportunities', value: formatNumber(row.total) },
        { label: 'Won', value: formatNumber(row.won) },
        { label: 'Value', value: formatNumber(Math.round(row.value)) },
        { label: 'Sub-accounts', value: formatNumber(row.subAccounts) },
      ],
    }));
  }, [summary]);

  const userBars = useMemo<BarDatum[]>(() => {
    if (!summary) return [];
    const rows = summary.byUser.filter((row) => row.userId).slice(0, 12);
    const nameCounts = new Map<string, number>();
    for (const row of rows) {
      const name = row.userName ?? row.userId!;
      nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    }
    return rows.map((row) => {
      const name = row.userName ?? row.userId!;
      const ambiguous = (nameCounts.get(name) ?? 0) > 1;
      return {
        key: `${row.userId}-${row.locationId}`,
        label: ambiguous && row.locationName ? `${name} · ${row.locationName}` : name,
        sublabel: row.locationName ?? undefined,
        value: row.total,
        tooltipRows: [
          { label: 'Opportunities', value: formatNumber(row.total) },
          { label: 'Won', value: formatNumber(row.won) },
          { label: 'Lost', value: formatNumber(row.lost) },
          { label: 'Value', value: formatNumber(Math.round(row.value)) },
        ],
      };
    });
  }, [summary]);

  if (!summary) {
    return <p className="empty">{loading ? 'Loading opportunities…' : 'No opportunity data yet — run a sync.'}</p>;
  }

  const { totals, previous } = summary;
  const createdChange = relativeChange(totals.opportunities, previous.opportunities);
  const totalPages = list ? Math.max(1, Math.ceil(list.total / pageSize)) : 1;
  const movement = summary.activity.reduce((sum, entry) => sum + entry.count, 0);

  return (
    <div className={loading ? 'stack reloading' : 'stack'}>
      <div className="grid grid-kpi">
        <StatTile
          label="Opportunities created"
          value={formatCompact(totals.opportunities)}
          hero
          delta={createdChange === null ? null : { change: createdChange, higherIsBetter: true }}
        />
        <StatTile
          label="Win rate"
          value={totals.resolved > 0 ? formatPercent(totals.winRate, 1) : '—'}
          footnote={
            totals.resolved > 0
              ? `${formatNumber(totals.won)} of ${formatNumber(totals.resolved)} decided`
              : 'Nothing has been won or lost yet'
          }
        />
        <StatTile
          label="Open"
          value={formatCompact(totals.open)}
          footnote={`${formatNumber(Math.round(totals.openValue))} in open value`}
        />
        <StatTile
          label="Won value"
          value={formatCompact(Math.round(totals.wonValue))}
          footnote={`${formatNumber(Math.round(totals.totalValue))} created in total`}
        />
        <StatTile
          label="Lost / abandoned"
          compact
          value={`${formatCompact(totals.lost)} / ${formatCompact(totals.abandoned)}`}
          footnote={totals.deleted ? `${formatNumber(totals.deleted)} deleted in HighLevel` : 'None deleted'}
        />
        <StatTile
          label="No appointment booked"
          value={formatCompact(totals.withoutAppointments)}
          footnote={`${formatNumber(totals.withUpcomingAppointments)} have one upcoming`}
        />
        <StatTile
          label="Recorded movements"
          value={formatCompact(movement)}
          footnote="Stage, status and owner changes in this window"
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Opportunities created per day by status</h2>
          <a className="btn btn-sm" href={api.opportunitiesCsvUrl(filters)} download>
            Export CSV
          </a>
        </div>
        <p className="card-sub">
          Bucketed by the day the opportunity was created, coloured by where it stands now — so a deal
          created in March and won in May appears on its March bar as Won.
        </p>
        <StackedColumnChart
          data={summary.timeseries}
          series={STATUS_SERIES}
          ariaLabel="Opportunities created per day by status"
        />
      </div>

      <div className="grid grid-2">
        <div className="card">
          <div className="card-head">
            <h2>Where they sit</h2>
            <span className="small muted">By stage</span>
          </div>
          <p className="card-sub">
            Stages in pipeline order, totalled across sub-accounts — each one has its own stage ids
            for the same named stage. Click a bar to filter to it.
          </p>
          <HorizontalBarChart
            data={stageBars}
            labelGutter={190}
            onSelect={(stageIds) => onFiltersChange({ ...filters, stageIds: stageIds.split(',') })}
          />
        </div>

        <div className="card">
          <div className="card-head">
            <h2>By owner</h2>
            <span className="small muted">Top 12</span>
          </div>
          <p className="card-sub">Who the opportunity is assigned to, not who moved it — see below.</p>
          <HorizontalBarChart data={userBars} labelGutter={210} />
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Opportunities</h2>
          <span className="small muted">{list ? `${formatNumber(list.total)} matching` : '—'}</span>
        </div>
        <p className="card-sub">
          Open <strong>Journey</strong> on any row for its full history: created, every stage move,
          every status change, reassignments and deletion. Appointment dates come from the contact —
          HighLevel links appointments to contacts, not deals — so a contact with several
          opportunities shows the same appointments against each.
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Created</th>
                <th>Opportunity</th>
                <th>Sub-account</th>
                <th>Contact</th>
                <th title="Out of 10, from the contact's DSCR form answers. Hover a rating for the breakdown.">
                  Lead rating
                </th>
                <th>Pipeline · stage</th>
                <th>Owner</th>
                <th>Appointments</th>
                <th>Status</th>
                <th className="num">Value</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(list?.opportunities ?? []).map((opportunity) => (
                <tr key={opportunity.id}>
                  <td className="nowrap">{formatDateTime(opportunity.createdAt)}</td>
                  <td>
                    <span className="cell-strong">{opportunity.name ?? '—'}</span>
                    {opportunity.deletedAt ? (
                      <div className="cell-sub" style={{ color: 'var(--danger-text)' }}>
                        deleted {formatDateTime(opportunity.deletedAt)}
                      </div>
                    ) : null}
                  </td>
                  <td>{opportunity.locationName ?? opportunity.locationId}</td>
                  <td>
                    {opportunity.contactId ? (
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => setJourneyContactId(opportunity.contactId)}
                        title="Open this contact's journey"
                      >
                        {opportunity.contactName ?? 'Open contact'}
                      </button>
                    ) : (
                      (opportunity.contactName ?? '—')
                    )}
                  </td>
                  <td className="nowrap">
                    <LeadScoreBadge lead={opportunity.leadScore} />
                  </td>
                  <td>
                    {opportunity.stageName ?? '—'}
                    {opportunity.pipelineName ? (
                      <div className="cell-sub">{opportunity.pipelineName}</div>
                    ) : null}
                  </td>
                  <td>{opportunity.assignedName ?? <span className="muted">Unassigned</span>}</td>
                  <td className="nowrap">
                    {opportunity.appointmentCount === 0 ? (
                      <span className="muted small">None booked</span>
                    ) : (
                      <>
                        {opportunity.nextAppointmentAt ? (
                          <div>
                            <span className="cell-sub">Next</span>{' '}
                            {formatDateTime(opportunity.nextAppointmentAt)}
                          </div>
                        ) : null}
                        {opportunity.lastAppointmentAt ? (
                          <div>
                            <span className="cell-sub">Last</span>{' '}
                            {formatDateTime(opportunity.lastAppointmentAt)}
                            {opportunity.lastAppointmentOutcome ? (
                              <span className="tag" style={{ marginLeft: 6 }}>
                                <span
                                  className="dot"
                                  style={{
                                    background:
                                      APPOINTMENT_OUTCOME_COLORS[opportunity.lastAppointmentOutcome],
                                  }}
                                  aria-hidden="true"
                                />
                                {APPOINTMENT_OUTCOME_LABELS[opportunity.lastAppointmentOutcome]}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                        {opportunity.appointmentsSharedWithSiblings ? (
                          <div
                            className="cell-sub"
                            title="This contact has other opportunities. HighLevel links appointments to contacts, not deals, so these are shared."
                          >
                            shared with other deals
                          </div>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td>
                    <StatusTag status={opportunity.status} />
                  </td>
                  <td className="num">{formatNumber(Math.round(opportunity.monetaryValue))}</td>
                  <td>
                    <button type="button" className="btn btn-sm" onClick={() => setJourneyId(opportunity.id)}>
                      Journey
                    </button>
                  </td>
                </tr>
              ))}
              {list && list.opportunities.length === 0 ? (
                <tr>
                  <td colSpan={11} className="empty">
                    No opportunities match these filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <div className="row">
            <label htmlFor="opp-page-size" className="muted">
              Rows
            </label>
            <select
              id="opp-page-size"
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {[25, 50, 100, 200].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <div className="row">
            <button type="button" className="btn btn-sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {summary.activity.length > 0 ? (
        <div className="card">
          <h2>Pipeline movement</h2>
          <p className="card-sub">Transitions recorded in this window, across every matching sub-account.</p>
          <div className="chips">
            {summary.activity.map((entry) => (
              <span className="chip" key={entry.event_type}>
                {OPPORTUNITY_EVENT_LABELS[entry.event_type] ?? entry.event_type}:{' '}
                <strong>{formatNumber(entry.count)}</strong>
              </span>
            ))}
          </div>
          <p className="small muted" style={{ marginTop: 12 }}>
            HighLevel does not report <strong>who</strong> made a change — no opportunity endpoint or
            webhook carries an actor — so the journey shows what changed and when, with the owner at
            the time, but not the person who did it.
          </p>
        </div>
      ) : null}

      {journeyId ? <JourneyDrawer opportunityId={journeyId} onClose={() => setJourneyId(null)} /> : null}
      {journeyContactId ? (
        <ContactJourneyDrawer contactId={journeyContactId} onClose={() => setJourneyContactId(null)} />
      ) : null}
    </div>
  );
}
